import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import {
    useClearWorkspaceRecommendationsMutation,
    useGetExtensionFolioQuery,
    useGetExtensionPotentialMatchesQuery,
    useGetWorkspaceExtensionMetadataQuery,
    useGetWorkspaceProductsQuery,
    useGetWorkspaceQuery,
    useRunDirectImportExtensionMutation,
    useRunExportExtensionMutation, 
    useSetWorkspaceExtensionFieldMutation
} from "../../store/Api";
import {
    IAdditionalData,
    IDirectImportItem,
    IExtensionResponse,
    IInstalledExtension,
    ISavedExtensionField
} from "../../models/Extension";
import PsaQuoteLinkDialog from "./PsaQuoteLinkDialog";
import {
    Checkbox,
    DefaultButton,
    IColumn,
    IconButton,
    MessageBar,
    MessageBarType,
    Modal,
    Panel,
    PanelType,
    Pivot,
    PivotItem,
    PrimaryButton,
    SelectionMode,
    ShimmeredDetailsList, Spinner,
    Stack,
} from "@fluentui/react";
import {ColumnGenerator} from "../../logic/Columns/ColumnGenerator";
import {IWorkspaceItem} from "../../models/Workspace";
import ExtensionItemDropdown from "./ExtensionItemDropdown";
import ExtensionMatchButton from "./ExtensionMatchButton";
import {
    IsFetchBaseQueryError,
    IsMutationError,
    IsMutationSuccess,
    IsSerializedError
} from "../../logic/MutationTypeChecker";
import ExtensionModalMessage from "./ExtensionModalMessage";
import {MODAL_ICON_BUTTON_STYLE, MODAL_STYLE} from "../../Constants";
import ExtensionField from "./ExtensionField";

interface IExtensionModalProps {
    extension: IInstalledExtension
    isOpen?: boolean;
    onClose: () => void;
    workspaceId: string;
    /** When provided, direct imports offer a "Prepare cart after import" option that closes the
     * modal and invokes this after a successful run (the host opens the prepared cart panel). */
    onPrepareCart?: () => void;
}

const ExtensionModal: React.FC<IExtensionModalProps> = (props) => {
    
    const closeAndRefreshParent = useSelector((state: RootState) => state.settings.refreshSource);
    
    const [running, setRunning] = useState(false);
    const [extensionResponse, setExtensionResponse] = useState<IExtensionResponse | undefined>();
    const [error, setError] = useState<string | undefined>();
    const [workspaceMetadata, setWorkspaceMetadata] = useState<ISavedExtensionField[]>()
    const [directImportSelections, setDirectImportSelections] = useState<IDirectImportItem[]>([])
    const [additionalData, setAdditionalData] = useState<IAdditionalData[]>([])
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedStepKey, setSelectedStepKey] = useState<string>();
    // Header slot the results grid portals its paginator into (panel mode only), so paging stays
    // visible while the list scrolls.
    const [paginatorHost, setPaginatorHost] = useState<HTMLDivElement | null>(null);
    // Direct-import behavior flags, all defaulting on.
    const [importBundles, setImportBundles] = useState<boolean>(true);
    const [attachContact, setAttachContact] = useState<boolean>(true);
    const [prepareCartAfter, setPrepareCartAfter] = useState<boolean>(true);
    const [quoteLinkOpen, setQuoteLinkOpen] = useState<boolean>(false);
    
    const [runExtension] = useRunExportExtensionMutation();
    const [runDirectImportExtension] = useRunDirectImportExtensionMutation();
    const [clearRecommendations] = useClearWorkspaceRecommendationsMutation();
    const [setWorkspaceExtensionField] = useSetWorkspaceExtensionFieldMutation();
    
    const getProducts = useGetWorkspaceProductsQuery(props.workspaceId, 
        {skip: (!props.workspaceId || props.workspaceId == '')});

    const potentialMatches = useGetExtensionPotentialMatchesQuery({
        extensionId: props.extension.id,
        products: getProducts.data?.map(d => ({catalog: d.catalog, id: d.productId})) ?? []}, 
        {skip: (!getProducts.data)});
    
    const existingExtensionWorkspaceMetadata = useGetWorkspaceExtensionMetadataQuery(
        {workspace: props.workspaceId, extension: props.extension.id},
        {skip: (!props.workspaceId || props.workspaceId.trim().length === 0)});
    
    const selectedWorkspace = useGetWorkspaceQuery(props.workspaceId,
        {skip: (!props.workspaceId || props.workspaceId.trim().length === 0)} )

    // Owning folio, for its quoteLink declaration (folio sub-extensions only).
    const extensionFolioForQuoteLink = useGetExtensionFolioQuery(props.extension.source,
        {skip: !props.extension.isSubExtension || !props.extension.source});
    
    useEffect(() => {
        if (existingExtensionWorkspaceMetadata.isFetching) {
            setLoading(true);
        } else {
            setLoading(false);
        }
        if (!existingExtensionWorkspaceMetadata.isFetching && existingExtensionWorkspaceMetadata.data) {
            setWorkspaceMetadata(existingExtensionWorkspaceMetadata.data)
        }
    }, [existingExtensionWorkspaceMetadata.data])
    
    useEffect(() => {
        setAdditionalData(props.extension.additionalData)
    }, [props.extension]);

    const checkIfActiveStep = (stepIdentifier: string) => {
        const step = props.extension.steps?.filter((a) => a.identifier == stepIdentifier)?.[0]
        if(!step) return true;
        if(!step.flag && (!step.requiredFields || step.requiredFields.length === 0)) return true;

        let isActive = true;
        const relevantField = additionalData.filter(f => f.field == step.flag)?.[0]

        if(relevantField) {
            const getWorkspaceMetadataField = workspaceMetadata?.filter(a => a.key == relevantField.field)?.[0]
            if (!getWorkspaceMetadataField) return true;
            isActive = getWorkspaceMetadataField?.value == "true";
        }
        if(step.requiredFields && step.requiredFields.length > 0) {
            for(const field of step.requiredFields){
                const getWorkspaceMetadataField = workspaceMetadata?.filter(a => a.key == field)?.[0]
                if(!getWorkspaceMetadataField || getWorkspaceMetadataField.value == ''){
                    isActive = false;
                    break;
                }
            }
        }

        return isActive;

    }

    // Resume on the furthest unlocked step when reopening an extension with saved answers (e.g. a company was
    // already chosen for step 1, so step 2 is where the user left off). One-shot: computed once the saved
    // workspace metadata arrives; after that the user navigates the tabs manually.
    useEffect(() => {
        if (selectedStepKey !== undefined) return;
        const steps = props.extension?.steps;
        if (!steps || steps.length === 0) return;
        if (existingExtensionWorkspaceMetadata.isFetching || !workspaceMetadata) return;
        let resume = steps[0].identifier;
        if (workspaceMetadata.length > 0) {
            for (const step of steps) {
                if (!checkIfActiveStep(step.identifier)) break;
                resume = step.identifier;
            }
        }
        setSelectedStepKey(resume);
    }, [workspaceMetadata, existingExtensionWorkspaceMetadata.isFetching]);

    if (props.extension === undefined || getProducts?.data == null) {
        return null;
    }

    const change = (field: string, value: string | boolean | undefined) => {
        if (props.workspaceId && props.extension.id) {
            setWorkspaceExtensionField({
                field: field,
                extension: props.extension.id,
                workspace: props.workspaceId,
                value: value?.toString(),
            })
        }
    }
    
    
    let itemTable = <></>;

    if (additionalData.find(f => f.fieldType === 'item')) {
        const itemColumns: IColumn[] = [
            {
                ...ColumnGenerator('Item', 'normalized.name', 100, undefined, (item: IWorkspaceItem) => <>{item.product?.name}</>),
                isMultiline: true
            },
            ColumnGenerator(
                'Existing Product',
                'existing',
                200,
                200,
                (item: IWorkspaceItem) =>
                    <ExtensionMatchButton
                        loading={potentialMatches.isFetching}
                        potentialMatches={potentialMatches.data}
                        item={item}
                        extension={props.extension}
                    />
            ),
            ...additionalData.filter(f => f.fieldType === 'item').map((data) => ({
                key: data.field,
                name: data.description,
                minWidth: 200,
                maxWidth: 200,
                onRender: (item: IWorkspaceItem) => {
                    return <>
                        <ExtensionItemDropdown
                            item={item}
                            field={data}
                            extension={props.extension}
                        />
                    </>
                }
            }))
        ];
        itemTable = <ShimmeredDetailsList
            selectionMode={SelectionMode.none}
            items={getProducts.data}
            enableShimmer={getProducts.isLoading}
            columns={itemColumns}
        />
    }

    const run = async () => {
        if (props.extension.extensionType === 'recommendation') {
            props.onClose();
            if (selectedWorkspace.data?.id) {
                await clearRecommendations({
                    extensionId: props.extension.id,
                    workspace: selectedWorkspace.data.id
                })
            }
        } 
        else if(props.extension.extensionType === 'directImport'){
            setRunning(true);
            runDirectImportExtension({
                extensionId: props.extension.id,
                request: {
                    catalog: props.extension.targetCatalog ?? "",
                    workspaceId: props.workspaceId,
                    items: directImportSelections,
                    importBundles: importBundles,
                    attachContact: attachContact}
            }).then((response) => {
                console.log(['RESPONSE', response])
                if (IsMutationSuccess<IExtensionResponse>(response)) {
                    if (response.data.success && prepareCartAfter && props.onPrepareCart) {
                        props.onClose();
                        props.onPrepareCart();
                        return;
                    }
                    setExtensionResponse(response.data);
                } else if (IsMutationError(response)) {
                    if (IsSerializedError(response.error))
                        setError(`Error serializing response: ${response.error.message}`)
                    else if (IsFetchBaseQueryError(response.error))
                        setError(`Error communicating with API: ${response.error.status}`)
                }
                setRunning(false);
            }).catch((ex) => {
                console.log(['EXCEPTION', ex]);
                setError(`Error running extension: ${ex}`);
            })
        }
        else {
            setRunning(true);
            runExtension({
                extensionId: props.extension.id,
                workspace: selectedWorkspace?.data?.id,
                products: getProducts?.data?.map(wsi => ({catalog: wsi.catalog, id: wsi.productId, quantity: wsi.details.quantity}))
            }).then((response) => {
                console.log(['RESPONSE', response])
                if (IsMutationSuccess<IExtensionResponse>(response)) {
                    setExtensionResponse(response.data);
                    if (closeAndRefreshParent) {
                        try {
                            console.log('Posting message to parent');
                            parent.postMessage({'func': 'CloseAdaptiveCatalog'}, '*');
                        } catch(e) {
                            //Do nothing
                            console.log('Error posting message to parent', e);
                        }
                    }
                } else if (IsMutationError(response)) {
                    if (IsSerializedError(response.error))
                        setError(`Error serializing response: ${response.error.message}`)
                    else if (IsFetchBaseQueryError(response.error))
                        setError(`Error communicating with API: ${response.error.status}`)
                }
                setRunning(false);
            }).catch((ex) => {
                console.log(['EXCEPTION', ex]);
                setError(`Error running extension: ${ex}`);
            })
        }
    }
    
    const disableDirectImport = () => {
        return props.extension.extensionType === 'directImport' && (!directImportSelections || directImportSelections.length === 0);
        
    }
    
    const actions =
        <div style={{ bottom:'0', right:'0', display: 'inline-flex', paddingTop: '10px'}}>
            <Stack tokens={{childrenGap: '1em'}} horizontal horizontalAlign='end' verticalAlign={'end'}>
                <PrimaryButton
                    onClick={run}
                    disabled={running || error !== undefined || extensionResponse !== undefined || disableDirectImport() }
                    text={running ? 'Running' : 'Run'}
                />
                <DefaultButton onClick={props.onClose} text='Close' />
            </Stack>
        </div>

    // The quote-link quick flow only offers itself when the owning folio declares it for this
    // sub-extension (the declaration carries the provider's install wording).
    const folioQuoteLink = extensionFolioForQuoteLink.data?.sourceExtensionFolio?.quoteLink;
    const offerQuoteLink = folioQuoteLink?.subExtension != null
        && props.extension.id === `folio_${props.extension.source}_${folioQuoteLink.subExtension}`;
    const quoteLinkButton = offerQuoteLink ? (
        <IconButton
            iconProps={{iconName: 'Link'}}
            title='PSA Quote Link setup'
            ariaLabel='PSA Quote Link setup'
            styles={{root: {marginLeft: '0.5em'}}}
            onClick={() => setQuoteLinkOpen(true)}
        />
    ) : null;

    const quoteLinkDialog = (
        <PsaQuoteLinkDialog
            isOpen={quoteLinkOpen}
            onClose={() => setQuoteLinkOpen(false)}
            extensionId={props.extension.id}
            folioId={props.extension.source}
            parameter={folioQuoteLink?.parameter}
            description={folioQuoteLink?.description}
            instructions={folioQuoteLink?.instructions}
        />
    );

    const importOptions = props.extension.extensionType === 'directImport' && !extensionResponse ? (
        <Stack horizontal wrap tokens={{childrenGap: '1em'}} verticalAlign='center' styles={{root: {marginBottom: '0.5em'}}}>
            <Checkbox label='Import bundles' checked={importBundles} disabled={running}
                onChange={(_, checked) => setImportBundles(checked === true)} />
            <Checkbox label='Attach contact & company' checked={attachContact} disabled={running}
                onChange={(_, checked) => setAttachContact(checked === true)} />
            {props.onPrepareCart &&
                <Checkbox label='Prepare cart after import' checked={prepareCartAfter} disabled={running}
                    onChange={(_, checked) => setPrepareCartAfter(checked === true)} />}
        </Stack>
    ) : null;

    const panelActions =
        <Stack horizontal tokens={{childrenGap: '1em'}}>
            <PrimaryButton
                style={{width: '200px'}}
                onClick={run}
                disabled={running || error !== undefined || extensionResponse !== undefined || disableDirectImport() }
                text={running ? 'Running' : 'Run'}
            />
            <DefaultButton
                style={{width: '200px'}}
                onClick={props.onClose}
                text='Close'
            />
        </Stack>
    
    const getSourceFieldValue = (fieldName: string | undefined) => {
        if(!fieldName) return undefined
        return existingExtensionWorkspaceMetadata?.data?.filter((a) => a.key == fieldName)?.[0]?.value ?? ""
    }
    const isPanel = props.extension.displayMode?.toLowerCase() === 'panel';
    const effectiveStepKey = selectedStepKey ?? props.extension.steps?.[0]?.identifier;

    const renderField = (m: IAdditionalData) => (
        <ExtensionField
            workspaceId={props.workspaceId}
            isLoading={loading}
            onChange={(a) => {change(m.field, a)}}
            key={`extension-field-${m.field}`}
            sourceFieldValue={getSourceFieldValue(m.parentField)}
            field={m}
            extension={props.extension}
            value={workspaceMetadata?.filter((a) => a.key == m.field)?.[0]?.value ?? ""}
            paginatorHost={isPanel ? paginatorHost : undefined}
            onExportSelection={(value) => {
                setDirectImportSelections(value)
            }}
        />
    );

    // The numbered step tabs alone — content renders separately so the panel can pin these in its header.
    const getStepTabs = () => {
        if (running || !props.extension.steps || props.extension.steps.length === 0) return null;
        return (
            <Pivot
                selectedKey={effectiveStepKey ?? null}
                onLinkClick={(item) => {
                    if (item?.props.itemKey) setSelectedStepKey(item.props.itemKey)
                }}>
                {props.extension.steps.map((s, i) =>
                    checkIfActiveStep(s.identifier)
                        ? <PivotItem key={s.identifier} itemKey={s.identifier} headerText={`${i + 1}.) ${s.name}`} />
                        : null)}
            </Pivot>
        );
    }

    const getStepContent = () => {
        if (running) {
            return <Spinner styles={{ root: { width: '400px', textAlign: 'center' } }} />;
        }
        if (!props.extension.steps) {
            return (
                <>
                    {additionalData.map((m) => renderField(m))}
                    {itemTable}
                </>
            )
        }
        const step = props.extension.steps.find(s => s.identifier === effectiveStepKey) ?? props.extension.steps[0];
        if (!step) return null;
        return (
            <>
                {additionalData.filter((f) => f.step == step.identifier).map((m) => renderField(m))}
                {step.showItems ? itemTable : <></>}
            </>
        );
    }
    
    const getExtensionModalContent = () => {
            return (
                <div style={{minWidth: '400px', minHeight: '350px'}}>
                    <div className={MODAL_STYLE.body}>
                        {extensionResponse && <ExtensionModalMessage response={extensionResponse} />}
                        {<><br/></>}
                        {!extensionResponse && <>
                            {!isPanel && getStepTabs()}
                            {props.extension.steps
                                ? <div style={isPanel
                                    ? {marginTop: '1em'}
                                    : {marginTop: '1em', height: '400px', width: '800px'}}>
                                    {getStepContent()}
                                </div>
                                : getStepContent()}
                        </>}
                    </div>
                </div>
            )
    }
    
    const getExtensionFooterContent = () => {
        return ( <div>{importOptions}{actions}</div>)
    }
    
    const getPanelExtensionFooterContent = () => {
        return <>{importOptions}{panelActions}</>;
    }

    return (<>
        {quoteLinkDialog}
        {props.extension.displayMode?.toLowerCase() != 'panel' &&
            <Modal
                isOpen={props.isOpen}
                onDismiss={() => props.onClose()}
                containerClassName={MODAL_STYLE.container}
                styles={{root:{overflow: 'visible', minHeight: '400px', minWidth: '400px'}}}
                
            >
                <div className={MODAL_STYLE.header}>
                    <span>{props.extension.name}</span>
                    {quoteLinkButton}
                    {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
                    <IconButton
                        styles={MODAL_ICON_BUTTON_STYLE}
                        iconProps={{iconName: 'Cancel'}}
                        ariaLabel="Close popup modal"
                        onClick={() => props.onClose()}
                    />
                </div>
                {getExtensionModalContent()}
                <div className={MODAL_STYLE.footer} style={{paddingTop: '80px'}}>{getExtensionFooterContent()}</div>
            </Modal>
        }
        {props.extension.displayMode?.toLowerCase() === 'panel' &&             
            <Panel
                isFooterAtBottom={true}
                className={"detail-panel"}
                isOpen={props.isOpen}
                onDismiss={() => props.onClose()}
                type={PanelType.large}
                onRenderFooterContent={getPanelExtensionFooterContent}
                onRenderHeader={() => (
                    <div style={{flexGrow: 1, minWidth: 0}}>
                        <div className={MODAL_STYLE.header}>
                            <span>{props.extension.name}</span>
                            {quoteLinkButton}
                            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
                        </div>
                        {!extensionResponse &&
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1em', padding: '0 24px'}}>
                                {getStepTabs()}
                                {/* ExtensionSearchResults portals its paginator here so paging stays visible while the list scrolls. */}
                                <div ref={setPaginatorHost} style={{flexShrink: 0}} />
                            </div>}
                    </div>
                )}
            >
                {getExtensionModalContent()}
            </Panel>
        }
        </>
    );
}

export default ExtensionModal;